import React, { useCallback, useRef, useState } from "react";
import Location from "react-autosuggest";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import Request from "axios-request-handler";
import classes from "./AutoComplete.module.css";
import { Urls } from "../../../../../../Urls";
import axios from "../../../../../../Axios/Axios";

export const requestInstance = new Request();

// classnames initializer
const classNames = {
  container: "react-autosuggest__container",
  input: "react-autosuggest__input",
  inputFocused: "react-autosuggest__input--focused",
  inputOpen: "react-autosuggest__input--open",
  suggestionsContainer: "react-autosuggest__suggestions-container",
  suggestionsContainerOpen: "react-autosuggest__suggestions-container--open",
  suggestionsList: "react-autosuggest__suggestions-list",
  suggestion: "react-autosuggest__suggestion",
  suggestionHighlighted: "react-autosuggest__suggestion--highlighted",
};

const AutoSuggestWrapper = styled.div.attrs(classNames)`
  ${classNames.container} {
    position: relative;
    margin: auto;
  }
  .${classNames.input} {
    width: 100%;
    height: 30px;
    padding: 10px 20px;
    font-family: Helvetica, sans-serif;
    font-weight: 300;
    font-size: 16px;
    border: 1px solid #aaa;
    border-radius: 4px;
    margin: 0px !important;
  }
  .${classNames.inputFocused} {
    outline: none;
  }
  .${classNames.inputOpen} {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .${classNames.suggestionsContainer} {
    display: none;
  }
  .${classNames.suggestionsContainerOpen} {
    display: block;
    width: 100%;
    border: 1px solid #aaa;
    background-color: #fff;
    font-family: Helvetica, sans-serif;
    font-weight: 300;
    font-size: 12px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    max-height: 100px;
    overflow-y: scroll;
    z-index: 100000;
    height: auto;
    white-space: nowrap;
  }
  .${classNames.suggestionsList} {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }
  .${classNames.suggestion} {
    cursor: pointer;
    padding: 3px 5px;
  }
  .${classNames.suggestionHighlighted} {
    background-color: #ddd;
  }
`;


/**
 * Component to render transporter name suggestions 
 * @param {Function} onSelect - function to trigger on select
 * @param {string} placeholder - placeholder for input field
 * @param {Function} onChange - fucntion to trigger on change of input
 * @param {string} value - value of input field
 * @param {string} key - key for component
 * @param {Function} getResults - function to trigger on results fetch
 * @returns {JSX.Element}
 */
const AutoComplete = ({
  onSelect,
  placeholder,
  onChange,
  value = "",
  key,
  getResults,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const [noSuggestions, setNoSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

   /**
   * Fetches suggestions based on the input value.
   * 
   * @param {string} value - input value to for fetching suggestions.
   */
  const fetchSuggestions = useCallback((value) => {
    if (value.length >= 3) {
      setShow(true);
      axios
        .post(`${Urls.Autocomplete.Get.getTransporterName}`, {
          transporter_name: value,
        })
        .then((res) => {
          if (res.data.transporter_data.length > 0) {
            setNoSuggestions(false);
            setSuggestions(res.data.transporter_data);
          } else {
            setNoSuggestions(true);
            setSuggestions([]);
          }
          setShow(false);
        })
        .catch((err) => {
          setShow(false);
          setNoSuggestions(true);
          setSuggestions([]);
        });
    } else {
      setNoSuggestions(false);
      setSuggestions([]);
    }
  }, []);

  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 500),
    [fetchSuggestions]
  );

  const onSuggestionsFetchRequested = ({ value }) => {
    debouncedFetchSuggestions(value);
  };

  /**
   * Function for clear suggestions
   */
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setNoSuggestions(false);
  };

   /**
   * function to set current value
   */
  const getSuggestionValue = (value) => {
    return value.transporter_name;
  };

  /**
   * Function to render suggestions
   * @param {string} value - current input value 
   * @returns {JSX.Element}
   */
  const renderSuggestion = (value) => {
    return value.transporter_name;
  };

  /**
   * Function to trigger on suggestion select
   */
  const onSuggestionSelected = (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }
  ) => {
    onChange(suggestion.transporter_name);
    onSelect(suggestion.transporter_name);
    if (getResults) getResults(suggestion);
  };

  const inputProps = {
    value: value,
    onChange: (e) => onChange(e.target.value),
    placeholder: placeholder,
    key: key,
    id: "Autosuggets_Input_id",
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false);
      setNoSuggestions(false);
    },
  };

  return (
    <AutoSuggestWrapper className={`position-relative`}>
      <Location
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        onSuggestionSelected={onSuggestionSelected}
        inputProps={inputProps}
        theme={classNames}
      />
      {show && <i className={`${classes["s-spin"]} fa fa-spinner fa-spin`} />}

      {noSuggestions && isFocused && (
        <div className={`${classes["no_suggestion"]}`}>
          No transporters found
        </div>
      )}
    </AutoSuggestWrapper>
  );
};

export default withRouter(AutoComplete);
